import { useContext, useMemo } from "react";
import { DeepPartial } from "react-hook-form";
import { useSelector } from "react-redux";
import { Option } from "space-monad";
import { AppStore } from "../../..";
import { unverifiableStatuses } from "../../../../pages/profile/kyc-common";
import { ConfigContext } from "../../../../pages/token-onboarding/single/config-context";
import { useFetchable } from "../../../common/fetchable";
import { isFetchableSuccess } from "../../../common/fetchable/helpers";
import { LaunchesDTO } from "../../../launches/models/dto";
import { Users } from "../../../users/reducer";
import { LaunchConfigurationsDTO } from "../dto";
import { useAddressAttachmentField } from "./address-attachment";
import { FieldConfigHook, FieldHook } from "./common";
import { useDestinationWalletField } from "./destination-wallet/destination-wallet";
import { useFaceAttachmentField } from "./face-attachment";
import { useIdentityDocumentField } from "./identity-document";
import { usePledgeField } from "./pledge";
import { useDeclarationStatementsField } from "./policies/declaration-statements";
import { useFinalDeclarationField } from "./policies/final-declaration";
import { useSekuritanceStatementsField } from "./policies/sekuritance-statements";
import { useWhitepaperReadField } from "./policies/whitepaper-read";
import { usePepField } from "./regulations/pep";
import { useResideSanctionedCountryField } from "./regulations/reside-sanctioned-country";
import { useUsPersonField } from "./regulations/us-person";
import { useSourceWalletField } from "./source-wallet";
import { useStakingWalletField } from "./staking-wallet/staking-wallet";
import { useAddressField } from "./user/address";
import { useUserApplicationPinField } from "./user/application-pin";
import { useCountryOfResidenceField } from "./user/country-of-residence";
import { useDobField } from "./user/date-of-birth";
import { useUserEmailField, useUserEmailFieldConfig } from "./user/email";
import { useNamesField } from "./user/names";
import { useNationalityField } from "./user/nationality";
import { usePhysicallyLocatedInField } from "./user/physically-located-in";

export const defaultFieldNames = [
  "user.emailAddress",
  "user.applicationPin",
  "regulations.pep",
  "pledge",
  "user.names",
  "user.dateOfBirth",
  "user.nationality",
  "user.countryOfResidence",
  "user.physicallyLocatedIn",
  "user.address",
  "addressAttachment",
  "identityDocument",
  "faceAttachment",
  "sourceWallet",
  "stakingWallet",
  "destinationWallet",
  "policies.whitepaperRead",
  "policies.declarationStatements",
  "regulations.usPerson",
  "policies.sekuritanceStatements",
  "regulations.resideSanctionedCountry",
  "policies.finalDeclaration",
] as const;

export type DefaultFieldNames = typeof defaultFieldNames[number];

export const useDefaultFieldNames: () => Record<
  DefaultFieldNames,
  string
> = () => {
  return useMemo(
    () => ({
      "user.emailAddress": "Email Address",
      "user.applicationPin": "Application PIN",
      "regulations.pep": "Pep",
      pledge: "Pledge",
      "user.names": "Names",
      "user.dateOfBirth": "Date Of Birth",
      "user.nationality": "Nationality",
      "user.countryOfResidence": "Country Of Residence",
      "user.physicallyLocatedIn": "Physically Located In",
      "user.address": "Address",
      addressAttachment: "Address Attachment",
      identityDocument: "Identity Document",
      faceAttachment: "Face Attachment",
      sourceWallet: "Source Wallet",
      stakingWallet: "Rewards Wallet",
      destinationWallet: "Destination Wallet",
      "policies.whitepaperRead": "Whitepaper Read",
      "policies.declarationStatements": "Declaration Statements",
      "regulations.usPerson": "US Person",
      "policies.sekuritanceStatements": "Sekuritance Statements",
      "regulations.resideSanctionedCountry": "Reside Sanctioned Country",
      "policies.finalDeclaration": "Final Declaration",
    }),
    []
  );
};

export const useDefaultFieldConfigMap: () => Partial<
  Record<DefaultFieldNames, ReturnType<FieldConfigHook>>
> = () => {
  const userEmailField = useUserEmailFieldConfig();

  return useMemo(
    () => ({
      "user.emailAddress": userEmailField,
    }),
    [userEmailField]
  );
};

export const useDefaultFieldMap: () => Record<
  DefaultFieldNames,
  ReturnType<FieldHook>
> = () => {
  const userEmailField = useUserEmailField();
  const userApplicationPinField = useUserApplicationPinField();
  const pepField = usePepField();
  const pledgeField = usePledgeField();
  const namesField = useNamesField();
  const dobField = useDobField();
  const nationalityField = useNationalityField();
  const countryOfResidenceField = useCountryOfResidenceField();
  const physicallyLocatedInField = usePhysicallyLocatedInField();
  const addressField = useAddressField();
  const addressAttachmentField = useAddressAttachmentField();
  const identityDocumentField = useIdentityDocumentField();
  const faceAttachmentField = useFaceAttachmentField();
  const sourceWalletField = useSourceWalletField();
  const stakingWalletField = useStakingWalletField();
  const destinationWalletField = useDestinationWalletField();
  const whitepaperReadField = useWhitepaperReadField();
  const declarationStatementsField = useDeclarationStatementsField();
  const sekuritanceStatementsField = useSekuritanceStatementsField();
  const finalDeclarationField = useFinalDeclarationField();
  const resideSanctionedCountryField = useResideSanctionedCountryField();
  const usPersonField = useUsPersonField();

  return useMemo(
    () => ({
      "user.emailAddress": userEmailField,
      "user.applicationPin": userApplicationPinField,
      "regulations.pep": pepField,
      pledge: pledgeField,
      "user.names": namesField,
      "user.dateOfBirth": dobField,
      "user.nationality": nationalityField,
      "user.countryOfResidence": countryOfResidenceField,
      "user.physicallyLocatedIn": physicallyLocatedInField,
      "user.address": addressField,
      addressAttachment: addressAttachmentField,
      identityDocument: identityDocumentField,
      faceAttachment: faceAttachmentField,
      sourceWallet: sourceWalletField,
      destinationWallet: destinationWalletField,
      stakingWallet: stakingWalletField,
      "policies.whitepaperRead": whitepaperReadField,
      "policies.declarationStatements": declarationStatementsField,
      "regulations.usPerson": usPersonField,
      "policies.sekuritanceStatements": sekuritanceStatementsField,
      "regulations.resideSanctionedCountry": resideSanctionedCountryField,
      "policies.finalDeclaration": finalDeclarationField,
    }),
    [
      userEmailField,
      userApplicationPinField,
      pepField,
      pledgeField,
      namesField,
      dobField,
      nationalityField,
      countryOfResidenceField,
      physicallyLocatedInField,
      addressField,
      addressAttachmentField,
      identityDocumentField,
      faceAttachmentField,
      sourceWalletField,
      stakingWalletField,
      destinationWalletField,
      whitepaperReadField,
      declarationStatementsField,
      sekuritanceStatementsField,
      finalDeclarationField,
      resideSanctionedCountryField,
      usPersonField,
    ]
  );
};

export function useAuthedFields(): Array<DefaultFieldNames> {
  const { config } = useContext(ConfigContext);

  return useMemo<Array<DefaultFieldNames>>(
    () => [
      "user.emailAddress",
      "user.names",
      "user.dateOfBirth",
      "user.nationality",
      "user.address",
      "addressAttachment",
      "identityDocument",
      ...(config.configuration.faceAttachmentRequired
        ? []
        : ["faceAttachment" as DefaultFieldNames]),
    ],
    [config]
  );
}

/** A hook so filter the pages needed based on the authentication status
 * of the user
 */
export function useFilteredPagesOnAuth(
  config: LaunchConfigurationsDTO.Responses.GetSingle.T,
  state: ConfigContext["state"]
) {
  const userDetails = useFetchable(Users.current);
  const authedFields = useAuthedFields();

  const currentFormValues = useSelector<
    AppStore,
    DeepPartial<LaunchesDTO.Requests.ParticipationForm.ForForm>
  >((store) => store.launches.newParticipationForm[config.id]);

  // Start by filtering fields based on some of the fields being only for unauthed users
  const filteredPagesBasedOnAuth = useMemo(() => {
    return config.configuration.merchantConfig.shouldUseShortFlow &&
      ((isFetchableSuccess(userDetails) &&
        userDetails.data.kycStatus === "VERIFIED") ||
        (state.userKycStatus &&
          ["VERIFIED", "IN_REVIEW", "PENDING"].includes(state.userKycStatus)))
      ? config.pages
        .map((page) => ({
          ...page,
          fields: page.fields.filter(
            (f) => !authedFields.find((x) => x === f.field)
          ),
        }))
        .filter(({ fields }) => fields.length > 0)
      : config.pages;
  }, [
    userDetails,
    isFetchableSuccess,
    unverifiableStatuses,
    config.pages,
    state.userKycStatus,
    config.configuration.merchantConfig.shouldUseShortFlow,
  ]);

  // Do further filtering based on current form, etc
  const filteredBasedOnOthers = useMemo(() => {
    const isPoaRequired =
      config.configuration.poaRequiredCountries?.includes("ALL") ||
      (currentFormValues?.user?.address?.country &&
        config.configuration.poaRequiredCountries?.includes(
          currentFormValues?.user?.address?.country
        ));

    // Sometimes, if user pledges more than a specified amount, we need proof of address
    const isPoALimitReached = Option(currentFormValues)
      .map((x) => x.pledge)
      .flatMap((x) => {
        return Option.all([x.amount, x.chain, x.currency]);
      })
      .flatMap(([amount, chain, currency]) => {
        return Option.all([
          config.configuration.cryptoChains?.[chain]?.pledgingInfo[currency]
            ?.amountExceededPoaRequirement,
          amount,
        ]);
      })
      .map(([limit, amount]) => amount >= limit)
      .getOrElse(false);

    // If POA is not required then hide addressAttachment
    return !isPoaRequired && !isPoALimitReached
      ? filteredPagesBasedOnAuth
        .map((page) => ({
          ...page,
          fields: page.fields.filter((f) => f.field !== "addressAttachment"),
        }))
        .filter(({ fields }) => fields.length > 0)
      : filteredPagesBasedOnAuth;
  }, [filteredPagesBasedOnAuth, currentFormValues]);

  return filteredBasedOnOthers;
}
